import { Component, Input, OnInit } from "@angular/core";
import { IAssigneeIdMap } from "@gtmhub/assignees";
import { ICadenceChangeParams } from "../cadence-pager/cadence-pager.component";
import { ITeam, ITeamsIdMap } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { extractUniqueTeamMemberIds, getLastDayOfCadence, getLastMondayAsDate, startWithFieldValue, toEnrichedContributorListMember } from "../../utils";
import { byEach, byTextField } from "@gtmhub/util/sorting-utils";
import { Ng1StateProxy } from "@gtmhub/core/routing";
import { ReflectionsFacade } from "@webapp/reflections/services/reflections-facade.service";
import { Observable, takeUntil } from "rxjs";
import { ICollection } from "@webapp/core/root/models/root.models";
import { IKrUpdate, IReflection, IReflectionUpdate, ReflectionQuestionType } from "@webapp/reflections/models/reflections.models";
import { ReflectionFormQuestion } from "@webapp/reflections/models/reflection-details.models";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import dayjs from "@gtmhub/dayjs";
import { IIndicator } from "@gtmhub/error-handling";
import { krUpdates } from "@webapp/reflections/services/mocks";
import { KeyMap, toKeyMap } from "@gtmhub/util";
import { IReflectionContributor } from "@webapp/reflections/models/reflection-contributor-list.models";
import { INotification } from "@gtmhub/notifications/models/notifications";
import { NotificationsFacade } from "@webapp/notification/services/notifications/notifications-facade.service";
import { NotificationsService } from "@gtmhub/notifications/services/notificationsService";

export const REFLECTION_CADENCE_DURATION_IN_DAYS = 7;

@Component({
  selector: "team-reflections",
  templateUrl: "./team-reflections.component.html",
})
export class TeamReflectionsComponent implements OnInit {
  @Input()
  public reflection: IReflection;

  // TODO: https://gtmhub.atlassian.net/browse/GVS-15530
  // Get teams and assignees from redux
  @Input()
  public teams: ITeamsIdMap;

  @Input()
  public assignees: IAssigneeIdMap;

  public indicators: {
    loading?: IIndicator;
  } = {};
  public members: IReflectionContributor[] = [];
  public activeMemberId: string;
  public cadenceStartDate: string;
  public cadenceDurationInDays = REFLECTION_CADENCE_DURATION_IN_DAYS;
  public latestAvailableDate: string;
  // TODO
  // should set this based on the cadance / userId
  public isAllowedToEditReflection = true;
  public reflectionUpdatesByUserIdMap: KeyMap<IReflectionUpdate, keyof IReflectionUpdate>;

  public reflectionFormQuestions: ReflectionFormQuestion<ReflectionQuestionType>[] = [];
  public reflectionFormKrUpdates: IKrUpdate[] = [];
  public currentReflectionUpdate?: IReflectionUpdate;
  public formTitle = "Product design (ux) reflection";
  public formSubTitle = "Week Jul 25th - Jul 31th";
  public shouldDisplayEmptyState = false;
  public shouldDisplayContributorList = true;
  public isUserTeamMember = false;

  constructor(
    private ng1StateProxy: Ng1StateProxy,
    private reflectionsFacade: ReflectionsFacade,
    private destroy$: DestroyService,
    private notificationsFacade: NotificationsFacade,
    private notificationsService: NotificationsService
  ) {}

  private team: ITeam;
  private cadenceEndDate: string;
  private reflectionNotificationsByUpdateIdMap: KeyMap<INotification, keyof INotification>;

  public ngOnInit(): void {
    this.indicators.loading = { progress: true };

    this.setTeam();
    this.setTeamMembers();

    const memberIdFromQuery = this.ng1StateProxy.getParam("memberId");
    this.activeMemberId = this.getInitialActiveMemberId(memberIdFromQuery);

    const cadenceStartFromQuery = this.ng1StateProxy.getParam("cadenceStart");
    const currentCadenceStartDate = this.getInitialCadenceStartDate(cadenceStartFromQuery);

    this.latestAvailableDate = getLastMondayAsDate();

    const urlContainsInvalidQuery = memberIdFromQuery !== this.activeMemberId || cadenceStartFromQuery !== currentCadenceStartDate;
    if (urlContainsInvalidQuery) {
      this.ng1StateProxy.go(
        ".",
        { ...this.ng1StateProxy.getParams(), memberId: this.activeMemberId, cadenceStart: dayjs(currentCadenceStartDate).format("YYYY-MM-DD") },
        { reload: false, notify: false, location: "replace" }
      );
    }

    const currentCadenceEndDate = getLastDayOfCadence(currentCadenceStartDate, REFLECTION_CADENCE_DURATION_IN_DAYS);

    this.setInitialCadenceStartAndEndDate({
      cadenceStartDate: currentCadenceStartDate,
      cadenceEndDate: currentCadenceEndDate,
    });

    this.getReflectionUpdatesForCadence({
      cadenceStartDate: this.cadenceStartDate,
      cadenceEndDate: this.cadenceEndDate,
    }).subscribe((reflectionUpdatesData) => {
      // users only have a single reflection update / cadence
      this.reflectionUpdatesByUserIdMap = toKeyMap("userId", reflectionUpdatesData.items);
      this.getReflectionUpdateNotificationsForCadence({ cadenceStartDate: this.cadenceStartDate, cadenceEndDate: this.cadenceEndDate }).subscribe((notificationData) => {
        this.reflectionNotificationsByUpdateIdMap = toKeyMap("targetId", notificationData.items);

        this.members = this.toEnrichedTeamMembersWithNotificationIndicator();

        if (reflectionUpdatesData.totalCount === 0) {
          this.checkIfAnyUpdateSubmittedEver();
          return;
        }

        this.prepareFormDataForCurrentReflectionUpdate();

        delete this.indicators.loading;
      });
    });
  }

  public displayReflectionForm(): void {
    this.shouldDisplayEmptyState = false;
    this.shouldDisplayContributorList = true;
  }

  public onMemberSelection(member: IReflectionContributor): void {
    this.setActiveMemberId(member.id);
    this.prepareFormDataForCurrentReflectionUpdate();

    this.markExistingNotificationAsSeen(member.id);
  }

  public onCadenceChange(params: ICadenceChangeParams): void {
    this.ng1StateProxy.go(
      ".",
      { ...this.ng1StateProxy.getParams(), cadenceStart: dayjs(params.cadenceStartDate).format("YYYY-MM-DD") },
      { reload: false, notify: false }
    );

    this.getReflectionUpdatesForCadence(params).subscribe((reflectionUpdatesData) => {
      this.reflectionUpdatesByUserIdMap = toKeyMap("userId", reflectionUpdatesData.items);
      this.getReflectionUpdateNotificationsForCadence(params).subscribe((notificationData) => {
        this.reflectionNotificationsByUpdateIdMap = toKeyMap("targetId", notificationData.items);

        this.members = this.toEnrichedTeamMembersWithNotificationIndicator();

        this.prepareFormDataForCurrentReflectionUpdate();
      });
    });
  }

  public isUserCurrentMember(): boolean {
    return this.activeMemberId === getCurrentUserId();
  }

  private setTeam(): void {
    const currentTeamId = this.ng1StateProxy.getParam("teamId");
    this.team = this.teams[currentTeamId];
  }

  private setTeamMembers(): void {
    const currentUserId = getCurrentUserId();
    const memberIds = extractUniqueTeamMemberIds(this.team);
    this.isUserTeamMember = memberIds.includes(currentUserId);

    this.members = memberIds
      .map((userId) => toEnrichedContributorListMember(this.assignees[userId]))
      .sort(byEach(startWithFieldValue("id", currentUserId), byTextField("name")));
  }

  private toEnrichedTeamMembersWithNotificationIndicator(): IReflectionContributor[] {
    return this.members.map((member) => {
      const reflectionUpdateNotification = this.getReflectionNotificationByUserId(member.id);
      const userHasNotSeenNotification = reflectionUpdateNotification && !reflectionUpdateNotification.seen;

      return { ...member, withDotIndicator: userHasNotSeenNotification };
    });
  }

  private getReflectionNotificationByUserId(memberId: string): INotification {
    const userReflectionUpdateId: string = this.reflectionUpdatesByUserIdMap[memberId] ? this.reflectionUpdatesByUserIdMap[memberId].id : null;
    return this.reflectionNotificationsByUpdateIdMap[userReflectionUpdateId];
  }

  private getInitialActiveMemberId(memberIdFromQuery: string): string {
    const isMemberIdValid = this.members.some((member) => member.id && member.id === memberIdFromQuery);
    const firstMemberId = this.members.length ? this.members[0].id : "";
    return isMemberIdValid ? memberIdFromQuery : firstMemberId;
  }

  private setActiveMemberId(memberId: string): void {
    this.activeMemberId = memberId;
    this.ng1StateProxy.go(".", { ...this.ng1StateProxy.getParams(), memberId: this.activeMemberId }, { reload: false, notify: false });
  }

  private markExistingNotificationAsSeen(memberId: string): void {
    const reflectionUpdateNotification = this.getReflectionNotificationByUserId(memberId);
    const userHasNotSeenNotification = reflectionUpdateNotification && !reflectionUpdateNotification.seen;

    if (userHasNotSeenNotification) {
      this.notificationsService.markAsSeen(reflectionUpdateNotification.id).then(() => {
        this.members = this.members.map((member) => {
          if (member.id === memberId) {
            return { ...member, withDotIndicator: false };
          }

          return member;
        });
      });
    }
  }

  private getInitialCadenceStartDate(cadenceStartFromQuery: string): string {
    const isValidDate = dayjs(cadenceStartFromQuery).isValid();
    if (!isValidDate) {
      return getLastMondayAsDate();
    }

    const isValidCadenceStart = dayjs(cadenceStartFromQuery).day() === 1;
    if (!isValidCadenceStart) {
      return getLastMondayAsDate();
    }

    return dayjs(cadenceStartFromQuery).toISOString();
  }

  private setInitialCadenceStartAndEndDate({ cadenceStartDate, cadenceEndDate }: ICadenceChangeParams): void {
    this.cadenceStartDate = cadenceStartDate;
    this.cadenceEndDate = cadenceEndDate;
  }

  private getReflectionUpdatesForCadence({ cadenceStartDate, cadenceEndDate }: ICadenceChangeParams): Observable<ICollection<IReflectionUpdate>> {
    const reqFilter = {
      filter: {
        $and: [{ createdAt: { $gte: cadenceStartDate } }, { createdAt: { $lte: cadenceEndDate } }, { teamId: { $in: [this.team.id] } }],
      },
    };

    return this.reflectionsFacade.getReflectionUpdates$(reqFilter).pipe(takeUntil(this.destroy$));
  }

  private checkIfAnyUpdateSubmittedEver(): void {
    const reqFilter = { filter: { teamId: { $in: [this.team.id] } }, fields: ["id"], skip: 0, take: 1 };

    this.reflectionsFacade
      .getReflectionUpdates$(reqFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((reflectionData) => {
        if (reflectionData.totalCount === 0) {
          this.shouldDisplayContributorList = false;
        }

        this.prepareFormDataForCurrentReflectionUpdate();

        delete this.indicators.loading;
      });
  }

  private getReflectionUpdateNotificationsForCadence({ cadenceStartDate, cadenceEndDate }: ICadenceChangeParams): Observable<ICollection<INotification>> {
    const reqFilter = {
      filter: {
        $and: [{ createdAt: { $gte: cadenceStartDate } }, { createdAt: { $lte: cadenceEndDate } }, { operationType: "user_reflection_update_published" }],
      },
    };

    return this.notificationsFacade.getNotificationsV2$(reqFilter).pipe(takeUntil(this.destroy$));
  }

  private prepareFormDataForCurrentReflectionUpdate(): void {
    this.setCurrentReflectionUpdate();
    this.shouldDisplayEmptyState = this.currentReflectionUpdate ? false : true;
    this.setReflectionFormQuestions();
    this.setReflectionsFormKrUpdates();
  }

  private setCurrentReflectionUpdate(): void {
    this.currentReflectionUpdate = this.reflectionUpdatesByUserIdMap[this.activeMemberId];
  }

  private setReflectionFormQuestions(): void {
    const answerByQuestionIdMap = this.currentReflectionUpdate ? toKeyMap("questionId", this.currentReflectionUpdate.answers) : {};

    this.reflectionFormQuestions = this.reflection.questions.map((question) => {
      return {
        text: question.text,
        answer: answerByQuestionIdMap[question.id]?.answer || "",
        type: question.type,
        ...(question.data && { data: question.data }),
      };
    });
  }

  private setReflectionsFormKrUpdates(): void {
    this.reflectionFormKrUpdates = krUpdates;
  }
}
